import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { formatMoney, priceBundle } from '../../utils/pricing';
import { planForPositions } from '../../utils/positions';
import { goToShop } from '../../utils/shopIntent';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
const MIN = 1;
const MAX = 12;
const QUICK = [2, 3, 4, 6, 12];

/** "How many lighting positions do you want?" — recommends installed + backup configurations. */
export function PositionsCalculator() {
  const {
    lang
  } = useLanguage();
  const reduce = useReducedMotion();
  const {
    startSystem
  } = useFunnel();
  const {
    setSelectedId
  } = useSystemSelection();
  const [positions, setPositions] = useState(4);
  const [withBackups, setWithBackups] = useState(true);
  const m = (n: number) => formatMoney(n, lang);
  const plan = planForPositions(positions, withBackups);
  const b = plan.bundle;
  const p = priceBundle(b);
  const badge = b.badge === 'mostPopular' ? 'Most popular' : b.badge === 'bestValue' ? 'Best value' : null;
  const set = (n: number) => setPositions(Math.max(MIN, Math.min(MAX, n)));
  return <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16" data-id="element-827" data-mp="2g40buqxqlvm">
      <div data-id="element-828" data-mp="2hx8jeh75e5q">
        <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-829" data-mp="gfc3szu4sg5m">Lighting positions</p>
        <div className="mt-3 flex items-center gap-4" data-id="element-830" data-mp="bfr06c40njmk">
          <button type="button" onClick={() => set(positions - 1)} disabled={positions <= MIN} aria-label="Fewer positions" className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 text-white transition-colors duration-150 hover:border-marine disabled:opacity-30" data-id="element-831" data-mp="bidc0nxxwlal">
            <MinusIcon className="h-5 w-5" aria-hidden data-id="element-832" data-mp="pfrpbel1kwkm" />
          </button>
          <output aria-live="polite" className="w-20 text-center font-display text-7xl font-semibold leading-none text-white" data-id="element-833" data-mp="tig19v51yjlt">
            {positions}
          </output>
          <button type="button" onClick={() => set(positions + 1)} disabled={positions >= MAX} aria-label="More positions" className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 text-white transition-colors duration-150 hover:border-marine disabled:opacity-30" data-id="element-834" data-mp="1riaiwm8x92z">
            <PlusIcon className="h-5 w-5" aria-hidden data-id="element-835" data-mp="fkhr71xrl9c4" />
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Common choices" data-id="element-836" data-mp="3e85btvyq3j8p">
          {QUICK.map(n => <button key={n} type="button" onClick={() => set(n)} aria-pressed={positions === n} className={`h-11 min-w-[3rem] rounded-md border px-4 font-display text-lg font-semibold transition-colors duration-150 ${positions === n ? 'border-marine bg-marine text-white' : 'border-white/15 text-titanium hover:border-marine/60 hover:text-white'}`} data-id="element-837" data-mp="3j5x3qpzdb5cw">
              {n}
            </button>)}
        </div>

        <label className="mt-8 flex cursor-pointer items-start gap-3" data-id="element-838" data-mp="2nz6b5plnxgwh">
          <input type="checkbox" checked={withBackups} onChange={e => setWithBackups(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#248DFF]" data-id="element-839" data-mp="2nwtnqwx4yec1" />
          <span data-id="element-840" data-mp="29z58q5pr21ew">
            <span className="block font-semibold text-white" data-id="element-841" data-mp="29zn3exwrk7kp">Include a charged backup set</span>
            <span className="block text-sm text-titanium" data-id="element-842" data-mp="24zimx7vuvcuh">Optional. One set installed, one set ready — swap instead of waiting to recharge.</span>
          </span>
        </label>

        {/* Position map: installed vs backup */}
        <div className="mt-8" aria-hidden data-id="element-843" data-mp="cx7k6nu0ifee">
          <div className="flex flex-wrap gap-1.5" data-id="element-844" data-mp="d0bv041zg9e1">
            {Array.from({
            length: b.qty
          }, (_, i) => <motion.span key={`${b.id}-${i}`} initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.2,
            delay: reduce ? 0 : Math.min(i * 0.02, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className={`h-6 w-6 rounded-full border-2 ${i < plan.installed ? 'border-marine bg-marine/30' : 'border-ice/50 bg-transparent'}`} data-id="element-845" data-mp="4678txxsnm4u" />)}
          </div>
          <p className="mt-3 flex gap-5 text-xs uppercase tracking-[0.12em] text-titanium" data-id="element-846" data-mp="1am9jqj6yhayf">
            <span className="inline-flex items-center gap-1.5" data-id="element-847" data-mp="1akezl73idmve">
              <span className="h-3 w-3 rounded-full border-2 border-marine bg-marine/30" data-id="element-848" data-mp="1fku9fool0c8m" /> Installed
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-849" data-mp="1pn6mk3fwf0dq">
              <span className="h-3 w-3 rounded-full border-2 border-ice/50" data-id="element-850" data-mp="1kr98y4w0s84n" /> Charged & ready
            </span>
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-851" data-mp="1tqw8el56v3v2">
        <motion.div key={`${b.id}-${positions}-${withBackups}`} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mf-hud grid overflow-hidden border border-marine bg-[#0D2440] sm:grid-cols-[180px_1fr]" data-id="element-852" data-mp="1tq2zbdirc29r">
          <img src={b.image} alt="" className="hidden h-full w-full object-cover object-[50%_30%] sm:block" data-id="element-853" data-mp="2delse66vvztq" />
          <div className="p-6 md:p-8" data-id="element-854" data-mp="n3h7gypxikr9">
            <div className="flex flex-wrap items-center gap-x-3" data-id="element-855" data-mp="n4iix4aux99i">
              <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-856" data-mp="j25pnfdar3tm">Recommended</p>
              {badge && <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ice" data-id="element-857" data-mp="2i7p0c6z00y1d">{badge}</p>}
            </div>
            <h3 className="mt-2 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em]" data-id="element-858" data-mp="2s3s0566dokl5">
              {b.tagline} — {b.qty} {b.qty === 1 ? 'light' : 'lights'}
            </h3>
            <ul className="mt-4 space-y-1 text-lg" data-id="element-859" data-mp="314gey33lioz3">
              <li className="text-white" data-id="element-860" data-mp="313ovfqn9whp6">
                <span className="font-semibold" data-id="element-861" data-mp="312m118qqrx4i">{plan.installed}</span> installed
              </li>
              {plan.backups > 0 && <li className="text-ice" data-id="element-862" data-mp="3au01biqip0yp">
                  <span className="font-semibold" data-id="element-863" data-mp="3awcp4ciyi5jo">{plan.backups}</span> {withBackups ? 'charged backups' : 'extra lights ready as spares'}
                </li>}
            </ul>
            {!plan.exact && <p className="mt-3 text-sm text-titanium" data-id="element-864" data-mp="3ou108sn81ybj">
                {withBackups ? `${positions} positions need ${positions * 2} lights with a full backup set. The ${b.tagline} is the closest official system, giving you ${plan.backups} charged backups.` : `The ${b.tagline} is the closest official system for ${positions} position${positions === 1 ? '' : 's'}.`}
              </p>}
            <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-865" data-mp="z7y3v48sc92c">
              <span className="font-display text-5xl font-semibold leading-none" data-id="element-866" data-mp="1wv6jwjq0opwy">{m(p.total)}</span>
              {p.savings > 0 && <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-867" data-mp="1rwmv8bk1h00w">Save {m(p.savings)}</span>}
            </div>
            <p className="mt-1 text-sm text-titanium" data-id="element-868" data-mp="1n30pz4g7fyf4">{m(p.perUnit)} per light</p>
            <div className="mt-3" data-id="element-869" data-mp="1i592qnqi90h9">
              <WarrantyTag tier={b.warranty} linked data-id="element-870" data-mp="1i3xvuf1rjclx" />
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-871" data-mp="2fmh7os0g4mkm">
              <button type="button" onClick={() => {
              track('positions_calculator_add', {
                positions,
                withBackups,
                bundleId: b.id,
                value: p.total,
                qty: b.qty
              });
              setSelectedId(b.id);
              startSystem(b.id);
            }} className={`${buttonClasses('primary')} w-full`} data-id="element-872" data-mp="2au66zmxvswr0">
                Add to cart — {m(p.total)}
              </button>
              <button type="button" onClick={() => goToShop(b.id)} className={`${buttonClasses('secondary')} w-full`} data-id="element-873" data-mp="1e5pbibp2v40t">
                Compare all systems
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>;
}